import React, { useState } from "react";
import { Link } from "react-router-dom";
import { getGitHubAuthUrl, getGitHubInstallUrl, getInstallationSettingsUrl, restoreTeam } from "../api";
import { useAuth } from "../auth/AuthContext";
import GitHubIcon from "./GitHubIcon";
import GitHubPermissionsNote from "./GitHubPermissionsNote";

/**
 * The two ways out of being teamless: start a team, or join one.
 *
 * Rendered in three places — the Dashboard's PR slot, /analytics and /team —
 * so it takes only the framing copy as props and owns the actions itself.
 * The pages differ in what they explain, never in what they offer; an e2e
 * test asserts all three present the same actions, because if they ever
 * diverge one screen is teaching a different way out than another.
 *
 * ⚠️ WHICH GITHUB URL IS CORRECT DEPENDS ON THE USER, AND THE CLIENT CANNOT
 * TELL. This panel used to always use the authorize URL, which made its
 * primary button a silent no-op for anyone who had never installed the App:
 * auth-callback only creates a team inside `if (installation)`, and discovery
 * finds nothing for them. Going the other way — always the install URL — dead
 * ends the opposite population on GitHub's configure screen (gotcha #29).
 *
 * The server resolves it. `installation_state` is computed in auth-callback
 * from installations it has already fetched, and is the certainty gotcha #29
 * asks for rather than a guess. Do not replace it with client-side detection;
 * there is nothing on the client that can distinguish these people.
 */
const STATES = {
  // No installation at all: they genuinely need GitHub's install screen.
  none: {
    label: "Install Anthurium on GitHub",
    href: () => getGitHubInstallUrl(),
    hint: "You'll pick which organization and repositories Anthurium can see.",
  },
  // An installation exists with no team attached — an abandoned install, or a
  // deletion whose uninstall step failed. Authorize round-trips through
  // auth-callback, whose discovery adopts it and creates the team.
  adoptable: {
    label: "Reconnect Anthurium and start a team",
    href: () => getGitHubAuthUrl(),
    hint: "Anthurium is still installed on your organization — we'll pick up where it left off. Your previous review history isn't recoverable.",
  },
  // Their installations already belong to teams. NEITHER GitHub URL works:
  // install dead-ends on the configure screen, and discovery refuses to
  // auto-join someone else's team. An invite code is the only real path, so
  // no GitHub button is offered at all.
  taken: {
    label: null,
    href: null,
    hint: "Anthurium is already installed on your organization and connected to an existing team. Ask a teammate for an invite code to join it.",
  },
};

/**
 * Set by TeamPage when a deletion's uninstall step failed. The dialog promised
 * the App would be removed, so a silent failure would leave a broken promise —
 * this carries the installation id forward just far enough to offer the manual
 * link once, on the screen they land on.
 */
const ORPHANED_INSTALLATION_KEY = "reviewflow_orphaned_installation";

/**
 * Turns the server's deletion_scheduled_for into "in 28 days" / "tomorrow".
 *
 * Relative rather than an absolute date because the only question the user has
 * is how long they have left. Floors to whole days, and never claims more time
 * than remains.
 */
function daysLeft(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return null;
  const days = Math.floor(ms / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
}

function TeamlessPanel({ title, body, pendingDeletion }) {
  const { user, pendingJoinCode, restoreCurrentTeam } = useAuth();
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState(null);

  // The restore lives here, not in the three pages, for the reason this file
  // already states about the start/join actions: the pages own copy, the panel
  // owns behaviour. Triplicating it would be three chances to diverge.
  const handleRestore = async () => {
    setRestoreError(null);
    setRestoring(true);
    try {
      const res = await restoreTeam();
      const data = res.data?.data || {};
      restoreCurrentTeam(data.session_token, data.team_id);
      // Full reload rather than a navigate: Dashboard, /analytics and /team
      // each hold their own cached getMyTeam() result, and a restore
      // invalidates all of them at once.
      window.location.reload();
    } catch (err) {
      setRestoring(false);
      // 410 (window elapsed) and 404 (nothing pending) both carry a specific
      // server message worth showing verbatim — "an error occurred" here would
      // leave someone unsure whether their data still exists.
      setRestoreError(err.response?.data?.error || err.message);
    }
  };
  // ⚠️ Read-ONCE. Nothing else clears this key, so without the remove it
  // persisted for the whole tab session: the warning re-rendered on every
  // teamless surface forever, including after the user had already removed the
  // app on GitHub — an un-dismissable notice about a problem that no longer
  // exists. Same read-once idiom as PENDING_REDIRECT_KEY.
  //
  // The initialiser runs once per mount, before paint, so the value is still
  // captured for this render.
  const [orphanedInstallation] = useState(() => {
    const value = sessionStorage.getItem(ORPHANED_INSTALLATION_KEY);
    sessionStorage.removeItem(ORPHANED_INSTALLATION_KEY);
    return value;
  });
  // Pre-filled so an invited user who lands here rather than on the wizard
  // doesn't silently lose their code.
  const [joinCode, setJoinCode] = useState(pendingJoinCode || "");

  // Falls back to the install URL when the field is missing — correct for the
  // majority, and it never dead-ends someone who has nothing installed. An
  // older session issued before this field existed lands here.
  const state = STATES[user?.installation_state] || STATES.none;

  const handleStart = () => {
    window.location.href = state.href();
  };

  const handleJoin = (e) => {
    e.preventDefault();
    if (!joinCode.trim()) return;
    // The code rides in OAuth's `state` so auth-callback can verify real
    // GitHub org access before joining (gotcha #23).
    window.location.href = getGitHubAuthUrl(joinCode.trim());
  };

  // ── A team awaiting restoration replaces the panel entirely ──────────────
  //
  // Not shown alongside the start/join actions, instead of them. Someone whose
  // team is one click from coming back should not be offered "start a team" as
  // a peer option — that is how you lose a month of history to a misread
  // screen. The two ways out are still reachable afterwards, because restoring
  // or letting the window lapse both return you to this panel's normal state.
  //
  // Handled here rather than in a fourth component so the invariant this file
  // already documents still holds: one shared component owns the actions, all
  // three surfaces get them identically, and no screen can teach a different
  // way out than another.
  if (pendingDeletion) {
    const remaining = daysLeft(pendingDeletion.deletion_scheduled_for);
    return (
      <div className="bg-rf-bg-secondary rounded-xl border border-rf-accent-warning/30">
        <div className="px-8 py-10">
          <div className="max-w-[52ch]">
            <h2 className="text-lg font-semibold text-rf-text-primary mb-2">
              {pendingDeletion.team_name} is scheduled for deletion
            </h2>
            <p className="text-sm text-rf-text-secondary leading-relaxed">
              Everything is still here — pull requests, review history and analytics. You can bring
              the team back{" "}
              {/* No date when the value is missing or already past: a dangling
                  "until ." reads as a bug, and claiming time that has already
                  elapsed is worse than saying nothing. */}
              {remaining ? (
                <>until it is permanently deleted <span className="text-rf-text-primary">{remaining}</span>.</>
              ) : (
                <>until it is permanently deleted.</>
              )}
            </p>

            <p className="mt-4 text-sm text-rf-text-secondary leading-relaxed">
              Restoring does not reinstall the GitHub App — that was removed when the team was
              deleted, so you'll need to install it again before new pull requests are picked up.
            </p>

            {restoreError && (
              <p className="mt-4 text-sm text-rf-accent-danger" data-testid="restore-error">
                {restoreError}
              </p>
            )}

            <button
              onClick={handleRestore}
              disabled={restoring}
              data-testid="restore-team"
              className="mt-6 px-5 py-2.5 rounded-lg bg-rf-accent text-rf-bg-primary font-semibold text-sm
                         hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {restoring ? "Restoring…" : `Restore ${pendingDeletion.team_name}`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-rf-bg-secondary rounded-xl border border-rf-border">
      <div className="px-8 py-10">
        <div className="max-w-[52ch]">
          <h2 className="text-lg font-semibold text-rf-text-primary mb-2">{title}</h2>
          <p className="text-sm text-rf-text-secondary leading-relaxed">{body}</p>

          <p className="mt-4 text-sm text-rf-text-secondary leading-relaxed">{state.hint}</p>

          {/* Only after a deletion whose uninstall genuinely failed. We said we
              would remove the App; we didn't; say so and hand over the link
              rather than letting them discover it on GitHub later. */}
          {orphanedInstallation && (
            <div className="mt-4 p-4 rounded-lg bg-rf-accent-warning/10 border border-rf-accent-warning/30">
              <p className="text-xs text-rf-accent-warning leading-relaxed">
                We couldn't remove Anthurium from your GitHub organization — your team was
                deleted, but the app is still installed.{" "}
                <a
                  href={getInstallationSettingsUrl(orphanedInstallation)}
                  target="_blank"
                  rel="noreferrer"
                  className="underline hover:no-underline"
                >
                  Remove it on GitHub →
                </a>
              </p>
            </div>
          )}

          {state.label && (
            <>
              <div className="mt-6">
                <GitHubPermissionsNote />
              </div>
              <button
                onClick={handleStart}
                className="mt-4 px-4 py-2.5 rounded-lg bg-rf-accent text-white font-semibold text-sm hover:bg-rf-accent/90 transition-colors flex items-center gap-2"
              >
                <GitHubIcon className="w-4 h-4" />
                {state.label}
              </button>
            </>
          )}

          <div className="mt-6 text-xs uppercase tracking-wider text-rf-text-secondary">
            {state.label ? "Or join an existing team" : "Join with an invite code"}
          </div>

          <form onSubmit={handleJoin} className="mt-2 flex items-end gap-2">
            <div className="flex-1 max-w-[320px]">
              <label
                htmlFor="teamless-invite-code"
                className="block text-xs text-rf-text-secondary mb-1.5"
              >
                Invite code
              </label>
              <input
                id="teamless-invite-code"
                type="text"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)}
                placeholder="Paste your invite code"
                className="w-full px-3.5 py-2.5 rounded-lg border border-rf-border bg-rf-bg-tertiary text-rf-text-primary text-sm font-mono outline-none focus:border-rf-accent-blue/50"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2.5 rounded-lg border border-rf-border bg-rf-bg-tertiary text-rf-text-primary font-semibold text-sm hover:border-rf-accent-blue/50 transition-colors"
            >
              Join
            </button>
          </form>

          {/* The wizard is the same actions with more explanation. It is a
              place you can go, not a mode you get trapped in — which is the
              whole point of it having a route. */}
          <p className="mt-6 text-xs text-rf-text-secondary">
            Prefer a guided setup?{" "}
            <Link to="/onboarding" className="text-rf-accent-blue hover:underline">
              Walk me through it
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
}

export default TeamlessPanel;
